/**
 * 業界別使い方ガイド一覧ページ
 *
 * 機能:
 * - 全業界の一覧表示
 * - 各業界の説明と主な機能
 * - デモアカウント情報の表示
 * - ログインページへのリンク
 */

import React from 'react';
import { Card, Row, Col, Typography, Button, Tag, Space, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { GlobalOutlined, RightOutlined, LoginOutlined, CopyOutlined } from '@ant-design/icons';
import { PublicPageLayout } from '../components/public/PublicPageLayout';
import { SEOHead } from '../components/SEOHead';

const { Paragraph, Text } = Typography;
const { useToken } = theme;

export default function IndustryGuidesPage() {
  const navigate = useNavigate();
  const { token } = useToken();

  // 業界情報をハードコード（本番環境でDBにデータがなくても表示できるように）
  const industriesWithDemoAccounts = [
    {
      id: 'factory',
      code: 'factory',
      name: '工場',
      nameEn: 'Factory',
      description: '製造業・工場向けの設定',
      features: ['設備の階層管理', '部品・消耗品在庫管理', '予防保全スケジューリング'],
      viewerEmail: 'demo-viewer-factory@example.com',
      adminEmail: 'demo-admin-factory@example.com',
      password: 'demo-viewer-2024',
    },
    {
      id: 'hotel',
      code: 'hotel',
      name: 'ホテル',
      nameEn: 'Hotel',
      description: 'ホテル・宿泊施設向けビルメンテナンス',
      features: ['客室設備管理', '宴会・厨房設備管理', 'リネン・アメニティ管理'],
      viewerEmail: 'demo-viewer-hotel@example.com',
      adminEmail: 'demo-admin-hotel@example.com',
      password: 'demo-viewer-2024',
    },
    {
      id: 'commercial_facility',
      code: 'commercial_facility',
      name: '商業施設',
      nameEn: 'Commercial Facility',
      description: 'ショッピングモール・商業ビル向けビルメンテナンス',
      features: ['共用部設備管理', 'エスカレーター・駐車場管理', 'テナント対応管理'],
      viewerEmail: 'demo-viewer-commercial_facility@example.com',
      adminEmail: 'demo-admin-commercial_facility@example.com',
      password: 'demo-viewer-2024',
    },
    {
      id: 'nursing_home',
      code: 'nursing_home',
      name: '介護施設',
      nameEn: 'Nursing Home',
      description: '介護施設・福祉施設向けビルメンテナンス',
      features: ['介護・医療設備管理', '衛生管理', 'ナースコールシステム管理'],
      viewerEmail: 'demo-viewer-nursing_home@example.com',
      adminEmail: 'demo-admin-nursing_home@example.com',
      password: 'demo-viewer-2024',
    },
    {
      id: 'educational_institution',
      code: 'educational_institution',
      name: '学校法人',
      nameEn: 'Educational Institution',
      description: '学校法人向けの設定',
      features: ['施設設備管理', '備品台帳管理', '業者管理'],
      viewerEmail: 'demo-viewer-educational_institution@example.com',
      adminEmail: 'demo-admin-educational_institution@example.com',
      password: 'demo-viewer-2024',
    },
    {
      id: 'municipality',
      code: 'municipality',
      name: '自治体',
      nameEn: 'Municipality',
      description: '自治体・公共施設向けの設定',
      features: ['設備管理', '資材管理', '点検・保守スケジュール'],
      viewerEmail: 'demo-viewer-municipality@example.com',
      adminEmail: 'demo-admin-municipality@example.com',
      password: 'demo-viewer-2024',
    },
  ];

  return (
    <>
      <SEOHead
        title="業界・施設別で見る一元帳の使い方"
        description="ホテル・工場など業界や施設ごとに異なる一元帳の使い方をデモアカウントで確認できます。設備管理の現場に合わせた活用イメージを具体的に把握できます。"
      />
      <PublicPageLayout
        title="業界別使い方ガイド"
      subtitle={
        <>
          <GlobalOutlined style={{ marginRight: '8px' }} />
          各業界に合わせた用語で、より使いやすく。デモアカウントで実際の画面を体験できます。
        </>
      }
    >
      <div style={{ marginBottom: '48px' }}>
        <Card
          style={{
            background: token.colorInfoBg,
            borderColor: token.colorInfoBorder,
            marginBottom: '32px',
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong style={{ fontSize: '16px', color: token.colorPrimary }}>
              デモアカウントについて
            </Text>
            <Paragraph style={{ marginBottom: '8px', color: token.colorTextSecondary }}>
              各業界のデモアカウントは<strong>閲覧専用</strong>です。
              実際の機能をお試しいただけますが、データの変更はできません。
            </Paragraph>
            <ul style={{ paddingLeft: '20px', marginBottom: 0, color: token.colorText }}>
              <li>メールアドレスとパスワードをクリックしてコピーできます</li>
              <li>全ての業界で同じパスワード「demo-viewer-2024」を使用</li>
            </ul>
          </Space>
        </Card>
      </div>

      <Row gutter={[24, 24]}>
        {industriesWithDemoAccounts.map((industry) => {
          return (
            <Col key={industry.id} xs={24} sm={12} lg={6}>
              <Card
                hoverable
                style={{
                  height: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: '1px solid #f0f0f0',
                }}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {/* 業界タグ */}
                  <div>
                    <Tag color="blue" style={{ fontSize: '13px', padding: '4px 12px' }}>
                      {industry.name}
                    </Tag>
                    {industry.nameEn && (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                        {industry.nameEn}
                      </Text>
                    )}
                  </div>

                  {/* 業界説明 */}
                  <Paragraph style={{ marginBottom: '0', fontSize: '14px' }}>
                    {industry.description}
                  </Paragraph>

                  {/* 主な機能 */}
                  <div>
                    <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                      主な機能:
                    </Text>
                    <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '13px' }}>
                      {industry.features.map((feature, idx) => (
                        <li key={idx} style={{ marginBottom: '4px', color: '#595959' }}>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* デモアカウント情報 */}
                  <Card
                    size="small"
                    style={{
                      background: '#f6ffed',
                      borderColor: '#b7eb8f',
                    }}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong style={{ color: '#389e0d', fontSize: '13px' }}>
                        <CopyOutlined /> デモアカウント
                      </Text>

                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          閲覧用:
                        </Text>
                        <Text
                          code
                          copyable
                          style={{
                            fontSize: '11px',
                            display: 'block',
                            marginTop: '4px',
                            wordBreak: 'break-all',
                          }}
                        >
                          {industry.viewerEmail}
                        </Text>
                      </div>

                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          パスワード:
                        </Text>
                        <Text
                          code
                          copyable
                          style={{
                            fontSize: '12px',
                            display: 'block',
                            marginTop: '4px',
                          }}
                        >
                          {industry.password}
                        </Text>
                      </div>
                    </Space>
                  </Card>

                  {/* アクションボタン */}
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Button
                      type="primary"
                      block
                      size="large"
                      icon={<LoginOutlined />}
                      onClick={() =>
                        navigate('/login', {
                          state: {
                            demoAccount: {
                              email: industry.viewerEmail,
                              password: industry.password,
                            },
                          },
                        })
                      }
                    >
                      デモを試す
                    </Button>
                    <Button
                      block
                      icon={<RightOutlined />}
                      onClick={() => navigate(`/industry-guides/${industry.code}`)}
                    >
                      詳細を見る
                    </Button>
                  </Space>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

    </PublicPageLayout>
    </>
  );
}
